import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * SyndicateBlock — appears at the bottom of every guide article.
 *
 * Offers editors and bloggers a copy-paste attribution snippet so they can
 * legally republish the full article under CC-BY-4.0 with one follow-link
 * back to the canonical URL. Every republication = one branded inbound link.
 *
 * The strategy: lower the friction of legitimate syndication so much that
 * publications choose to republish + cite instead of scraping uncredited.
 */
export default function SyndicateBlock({ title, slug }) {
  const [copied, setCopied] = useState(false);
  const canonicalUrl = `https://havencostguide.com/guides/${slug}`;

  // The attribution snippet editors paste at the top or bottom of the
  // republished article. Includes title, canonical follow-link, and license.
  const attribution = `This article was originally published by HavenCostGuide as "${title}" and is republished here under a Creative Commons Attribution 4.0 license. Original article: ${canonicalUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(attribution);
      setCopied(true);
      toast.success("Attribution snippet copied", {
        description: "Paste it at the top of your republished article.",
      });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — please select the text manually.");
    }
  };

  return (
    <aside
      className="mt-14 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8"
      data-testid="syndicate-block"
    >
      <div className="flex items-start gap-3 sm:items-center">
        <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white sm:flex">
          <FileText className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
            Republish this article — free, with attribution
          </p>
          <h2 className="mt-1 font-display text-xl font-semibold text-slate-900">
            Editor? You can run this on your site.
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            This article is published under{" "}
            <a
              href="https://creativecommons.org/licenses/by/4.0/"
              target="_blank"
              rel="noopener"
              className="font-medium text-green-700 underline-offset-4 hover:underline"
            >
              Creative Commons Attribution 4.0
            </a>
            . Republish the full text on your site at no cost — just include
            the attribution snippet below and a follow-link to the original.{" "}
            <Link
              to="/license"
              className="font-medium text-green-700 underline-offset-4 hover:underline"
              data-testid="syndicate-license-link"
            >
              See full terms →
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Attribution snippet
        </p>
        <p className="mt-2 font-mono text-xs leading-relaxed text-slate-700 sm:text-sm">
          {attribution}
        </p>
        <button
          onClick={handleCopy}
          type="button"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white transition hover:bg-slate-800 sm:text-sm"
          data-testid="syndicate-copy-button"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" /> Copy attribution snippet
            </>
          )}
        </button>
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Need a custom data export, exclusive metro-level breakdown, or want to
        interview our research lead?{" "}
        <Link
          to="/press"
          className="font-medium text-slate-700 underline-offset-4 hover:underline"
          data-testid="syndicate-press-link"
        >
          See the Press Kit →
        </Link>
      </p>
    </aside>
  );
}
